import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IMapAddress {
    fullAddress: string;
    address1: string;
    city: string;
    state: string;
    country: string;
    postal: string;
    latitude: string;
    longitude: string;
    stateCode: string;
    countryCode: string;
    companyName?: string;
    streetNumber?: string | null;
}

export interface DimensionsData {
    id: number | null;
    width: number;
    height: number;
    weight: number;
    handlingUnit: string;
    handlingUnitNo: number;
    totalWeight: number;
    freightClass: number;
    isCommodity: boolean;
    isStack: boolean;
    weightMeasurement: string;
    commodityId: number | null;
    commodityName: string | null;
    sub: string | null;
    description: string | null;
    itemLength: number;
}

// State interface
interface BookingStepperState {
    currentStep: number;
    isHiddenFieldInBooking: boolean;
    completedSteps: number[];
    formData: Record<string, any>;
    isLoading: boolean;
    errors: Record<string, string>;
}

// Initial state
const initialState: BookingStepperState = {
    currentStep: 1,
    isHiddenFieldInBooking: true,
    completedSteps: [],
    formData: {
        quoteId: '',
        quoteCarrierId: '',
        shipperDetails: {
            pickupDate: '',
            shipperId: null,
            shipperCompanyName: '',
            shipperFullAddress: '',
            shipperAddress1: '',
            shipperStreetNumber: '',
            shipperCity: '',
            shipperState: '',
            shipperStateCode: '',
            shipperCountry: '',
            shipperPostal: '',
            shipperLatitude: '',
            shipperLongitude: '',
            shipperCountryCode: '',
            shipperContactId: null,
            shipperContactName: '',
            shipperContactPhone: '',
            shipperContactEmail: '',
            pickupInstruction: '',
            pickupTimeType: 'appointment',
            pickupStartTime: '',
            pickupEndTime: '',
        },
        consigneeDetails: {
            consigneeId: null,
            consigneeCompanyName: '',
            consigneeFullAddress: '',
            consigneeAddress1: '',
            consigneeStreetNumber: '',
            consigneeCity: '',
            consigneeState: '',
            consigneeStateCode: '',
            consigneeCountry: '',
            consigneePostal: '',
            consigneeLatitude: '',
            consigneeLongitude: '',
            consigneeCountryCode: '',
            consigneeContactId: null,
            consigneeContactName: '',
            consigneeContactPhone: '',
            consigneeContactEmail: '',
            consigneeContactNumber: '',
            deliveryTimeType: 'appointment',
            deliveryStartTime: '',
            deliveryEndTime: '',
            deliveryInstruction: '',
        },
        orderDetails: {
            orderType: '',
            equipmentType: '',
            equipmentTypeFullName: '',
            poNumber: '',
            refNumber: '',
            pickupNumber: '',
            deliveryNumber: '',
            isLoadInsuranceEnabled: false,
            insuranceCommodity: null,
            insuranceCommercialValue: null,
            insuranceCurrencyType: 'CAD',
        },
        uploadedDocuments: [],
        carrierSlug: null as string | null,
        bolRequirementsData: {} as Record<string, string>,
    },
    isLoading: false,
    errors: {},
};

// Create slice
export const bookingStepperSlice = createSlice({
    name: 'bookingStepper',
    initialState,
    reducers: {
        setCurrentStep: (state, action: PayloadAction<number>) => {
            state.currentStep = action.payload;
        },
        nextStep: (state) => {
            // Bug fix (user-reported) — this used to hardcode a cap of 7 (BOOKING_STEPS' fixed
            // length before "Stop N" steps existed). getFilteredBookingSteps now injects a
            // variable number of stop steps, so the real last-step index isn't knowable here (this
            // slice has no visibility into isCrossBorder/stopPositions). BookingStepper/index.tsx's
            // handleNext already checks `currentStep === steps.length` BEFORE ever dispatching this
            // — it only fires when there's a real next step to go to — so no upper bound is needed
            // here at all; the old cap was silently trapping users on step 7 forever once a stop
            // step pushed Review & Confirm past it.
            state.currentStep = state.currentStep + 1;
        },
        prevStep: (state) => {
            state.currentStep = Math.max(state.currentStep - 1, 1);
        },
        goToStep: (state, action: PayloadAction<number>) => {
            // Bug fix (user-reported) — same stale hardcoded-7 issue as nextStep above. Callers
            // (Sidebar's step click, validateRequiredStepsBeforeBooking's failingStep) always pass
            // an id sourced from the current (already stop-aware) `steps` array, so only the lower
            // bound needs guarding here.
            state.currentStep = Math.max(1, action.payload);
        },
        completeStep: (state, action: PayloadAction<number>) => {
            if (!state.completedSteps.includes(action.payload)) {
                state.completedSteps.push(action.payload);
            }
        },
        setQuoteBookingFormData: (state, action: PayloadAction<Record<string, any>>) => {
            state.formData = {
                ...state.formData,
                ...action.payload,
            };
        },
        addUploadedDocument: (state, { payload }) => {
            if (!state.formData.uploadedDocuments) {
                state.formData.uploadedDocuments = [];
            }
            const existingIndex = state.formData.uploadedDocuments.findIndex((doc: any) => doc.id === payload.id);

            if (existingIndex !== -1) {
                // Update existing entry (e.g. flip isUploading/isDeleting flags)
                state.formData.uploadedDocuments[existingIndex] = payload;
            } else {
                state.formData.uploadedDocuments.push(payload);
            }
        },
        removeUploadedDocument: (state, { payload }: PayloadAction<{ id: string }>) => {
            if (Array.isArray(state.formData.uploadedDocuments)) {
                state.formData.uploadedDocuments = state.formData.uploadedDocuments.filter((doc: any) => doc.id !== payload.id);
            }
        },
        resetForm: () => initialState,
    },
});

// Export actions
export const { setCurrentStep, nextStep, prevStep, goToStep, completeStep, setQuoteBookingFormData, addUploadedDocument, removeUploadedDocument, resetForm } = bookingStepperSlice.actions;

// Export reducer
export default bookingStepperSlice.reducer;

// Selectors
export const selectBookingStepper = (state: any) => state.bookingStepper;
export const selectIsHiddenFieldInBooking = (state: any) => state.bookingStepper?.isHiddenFieldInBooking ?? true;
export const selectCurrentStep = (state: any) => state.bookingStepper.currentStep;
export const selectCompletedSteps = (state: any) => state.bookingStepper.completedSteps;
export const selectFormData = (state: any) => state.bookingStepper.formData;
export const selectIsLoading = (state: any) => state.bookingStepper.isLoading;
export const selectErrors = (state: any) => state.bookingStepper.errors;
